import { useEffect, useState } from "react";
import { Button, FormRow, Input } from "@omnabase/ui";
import { parseSendingAddress, sendingAddressPrefill } from "@omnabase/shared";

/** The own-domain setup's first field (the first setup round, item 5, 2026-10-22): the whole sending address, "Replies come from", offered
 *  from the receiving step's support address when that is on a domain of the customer's own (never ours, never a public mail provider).
 *  "Continue" adds the domain the address names; a bare domain is completed with "support@". A refusal says why, in the field's help. */
export type SendingAddress = { local: string; domain: string };

/** `current`: the address of a domain already added and not yet verified (the field stays above its records, so a typo can be fixed) */
export function SendingAddressForm({ supportAddress, current, onContinue }: { supportAddress: string | null; current?: string | null; onContinue: (a: SendingAddress) => Promise<boolean> }) {
  const offer = current ?? sendingAddressPrefill(supportAddress);
  const [value, setValue] = useState(offer ?? "");
  const [touched, setTouched] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!touched && offer) setValue(offer); }, [offer, touched]); // the Mail card's read may arrive after the form
  const parsed = parseSendingAddress(value);
  const submit = async () => {
    if (!parsed.ok) { setProblem(parsed.problem); return; }
    setProblem(null); setBusy(true);
    try { if (await onContinue({ local: parsed.local, domain: parsed.domain })) { setValue(parsed.address); setTouched(false); } } finally { setBusy(false); }
  };
  const help = problem ?? (parsed.ok ? `The records go on ${parsed.domain}.` : "The address your replies should come from, for example support@example.com.");
  return (
    <form className="ob-dns__add" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <FormRow label="Replies come from" audience="admins" stack help={<span className={problem ? "ob-dns__problem" : undefined} role={problem ? "alert" : undefined}>{help}</span>}>
        <span className="ob-dns__add-controls">
          <Input className="ob-input--text" aria-label="Replies come from" placeholder="support@example.com" value={value} onChange={(e) => { setTouched(true); setProblem(null); setValue(e.target.value); }} autoComplete="off" spellCheck={false} />
          <Button variant="primary" type="submit" busy={busy}>Continue</Button>
        </span>
      </FormRow>
    </form>
  );
}
